import {
  FormGroup,
  Form,
  IconNotificationSuccess,
  IconNotificationWarning,
  Input,
  Modal,
  Button,
  Icons,
} from '@snsw/react-component-library';
import { GelTheme } from 'components/GelTheme';
import styled, { css } from 'styled-components/macro';
import { ModalAlert } from 'components/VerifiableContactDetailField/components/ModalAlert';
import { VerifiableContactDetailApi } from 'components/VerifiableContactDetailField/components/VerifiableContactDetailApi';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAsyncFn, useBoolean, useMount } from 'react-use';
import type { ContactDetailType, ContactMethodVerificationStatus } from 'components/VerifiableContactDetailField/types';
import classes from 'components/VerifiableContactDetailField/components/VerifiableContactDetailModal.module.scss';

interface VerifiableContactDetailModalProps {
  type: ContactDetailType;
  value: string;

  sendCodeOnMount: boolean;
  onCodeSent: () => void;
  allowSkip?: boolean;
  onVerifyCodeFailed: () => void;

  onCancel: () => void;
  onComplete: (status: ContactMethodVerificationStatus, response?: object | boolean) => void;

  api: VerifiableContactDetailApi;
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: ${GelTheme.spacing(1.5)};
`;

const CodeInput = styled(Input)`
  font-size: 2rem;
  font-weight: ${GelTheme.fontWeight('bold')};
  letter-spacing: 0.625rem;
  text-align: center;
`;

const VerifyButton = styled(Button)<{ verifyingCode?: boolean }>`
  border-top: 1px solid ${GelTheme.color('geyser')};
  padding-top: ${GelTheme.spacing(1)};

  ${(props) =>
    props.verifyingCode &&
    css`
      padding: ${GelTheme.spacing(0.5)} 0 0;

      svg {
        width: ${GelTheme.spacing(2)};
        height: ${GelTheme.spacing(2)};
      }
    `}
`;

const CenteredButton = styled(Button)`
  align-self: center;
`;

const Spinner = styled(Icons.Spinner)`
  width: ${GelTheme.spacing(2)};
  height: ${GelTheme.spacing(2)};
  align-self: center;
`;

export const VerifiableContactDetailModal = ({
  type,
  value,
  sendCodeOnMount,
  onCodeSent,
  allowSkip,
  onVerifyCodeFailed,
  onCancel,
  onComplete,
  api,
}: VerifiableContactDetailModalProps) => {
  const inputRef = useRef<HTMLInputElement>();

  const [code, setCode] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const [skipped, toggleSkipped] = useBoolean(false);

  const [{ loading: sendingCode }, handleSendCode] = useAsyncFn(async () => {
    setErrorMessage(undefined);

    try {
      await api.sendCode(value);
      onCodeSent();
    } catch (err) {
      setErrorMessage((err as Error).message);
    }
  }, [value]);

  const [{ loading: verifyingCode, value: verified }, handleVerify] = useAsyncFn(async () => {
    setErrorMessage(undefined);

    try {
      return await api.verifyCode(value, code);
    } catch (err) {
      setErrorMessage((err as Error).message);
      onVerifyCodeFailed();
    }
  }, [value, code]);

  useMount(async () => sendCodeOnMount && (await handleSendCode()));

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: next } = event.target;
    if (next.length <= 10) setCode(event.target.value);
  };

  const handleVerified = () => onComplete('verified', verified);
  const handleSkipConfirmed = () => onComplete('skipped');

  const typeName = type === 'email' ? 'email address' : 'mobile number';
  const title = sendingCode
    ? `A verification code is being sent to ${value}`
    : `A verification code has been sent to ${value}`;

  const busy = sendingCode || verifyingCode;
  useEffect(() => {
      !busy && inputRef.current?.focus();
     }, [busy]);

  return (
    <>
      {skipped && (
        <ModalAlert onClose={handleSkipConfirmed}>
          <IconNotificationWarning size="md" />
          {`Your ${typeName} is not verified`}
        </ModalAlert>
      )}

      {!!verified && (
        <ModalAlert onClose={handleVerified}>
          <IconNotificationSuccess size="md" />
          {`Your ${typeName} was successfully verified`}
        </ModalAlert>
      )}

      {!(skipped || verified) && (
        <Modal title={title} onClose={onCancel} size="sm" className={classes.verifyModal} buttons={[]}>
          <Form onSubmit={handleVerify}>
            <ModalContent>
              <FormGroup
                id="codeVerification"
                label="Please enter the code below"
                hasError={!!errorMessage}
                errorMessage={errorMessage}
                disabled={busy}
              >
                <CodeInput value={code} disabled={busy} onChange={handleCodeChange} inputRef={inputRef} />
              </FormGroup>

              <VerifyButton type="submit" onClick={handleVerify} disabled={busy} verifyingCode={verifyingCode}>
                {verifyingCode && <Spinner color={GelTheme.color('lightGrey3')} />}
                {!verifyingCode && <>Submit</>}
              </VerifyButton>

              {!sendingCode && (
                <CenteredButton variant="link" disabled={busy} onClick={handleSendCode}>
                  Resend code
                </CenteredButton>
              )}

              {sendingCode && <Spinner color={GelTheme.color('lightGrey3')} />}

              {allowSkip && (
                <CenteredButton variant="link" disabled={busy} onClick={toggleSkipped}>
                  Skip
                </CenteredButton>
              )}
            </ModalContent>
          </Form>
        </Modal>
      )}
    </>
  );
};
